import { Col, Form, FormInstance, Input, Radio, Row } from 'antd';
import { FormListFieldData } from 'antd/lib/form/FormList';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { IGeneralSettings } from '../models/generalSettingsModel';
import { ServerValidationStatus } from '../utils/validationStatusEnum';
import { useConfigurationCtx } from './ConfigurationContext';
import { IGeneralSettingsForm } from './GeneralSettings';

export interface ServerCredentialsFormProps {
  field: FormListFieldData;
  disabled: boolean;
  form: FormInstance<IGeneralSettingsForm>;
}

export const ServerCredentialsForm = ({ field, disabled, form }: ServerCredentialsFormProps) => {
  const [t] = useTranslation('geosecma-settings');
  const { generalSettings } = useConfigurationCtx();

  const handleOnChange = () => {
    form.setFields([
      { name: ['serverInfos', field.name, 'validationStatus'], value: ServerValidationStatus.notValidated }
    ]);
  };

  return (
    <Row gutter={16}>
      <Col span={24}>
        <Form.Item
          label={t('loginType')}
          {...field}
          name={[field.name, 'loginType']}
          fieldKey={[field.key, 'loginType']}
        >
          <Radio.Group
            aria-label={t('loginType')}
            disabled={disabled}
            buttonStyle="solid"
            name="loginType"
            onChange={handleOnChange}
            data-testid={`loginTypeRadioGroup${field.name}`}
          >
            <Radio value="noLogin" data-testid={`noLoginRadioButton${field.name}`}>
              {t('noLogin')}
            </Radio>
            <Radio value="username/password" data-testid={`passwordRadioButton${field.name}`}>
              {t('username/password')}
            </Radio>
            {!generalSettings.hideToken ? (
              <Radio value="token" data-testid={`tokenRadioButton${field.name}`}>
                {t('token')}
              </Radio>
            ) : null}
          </Radio.Group>
        </Form.Item>
      </Col>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues: IGeneralSettings, currentValues: IGeneralSettings) => {
          const currentServerInfo = currentValues.serverInfos?.[field.name];
          const prevServerInfo = prevValues.serverInfos?.[field.name];
          if (!currentServerInfo) {
            return false;
          }
          if (prevServerInfo?.loginType !== currentServerInfo.loginType) {
            form.setFields([{ name: ['serverInfos', field.name, 'username'], value: '' }]);
            form.setFields([{ name: ['serverInfos', field.name, 'password'], value: '' }]);
          }

          return prevServerInfo?.loginType !== currentServerInfo.loginType;
        }}
      >
        {({ getFieldValue }) =>
          getFieldValue(['serverInfos', field.name, 'loginType']) === 'username/password' ? (
            <>
              <Col xs={24} lg={12}>
                <Form.Item
                  {...field}
                  name={[field.name, 'username']}
                  fieldKey={[field.key, 'username']}
                  label={t('username')}
                  rules={[
                    {
                      required: true,
                      message: `${t('username')} ${t('required')}`
                    }
                  ]}
                >
                  <Input disabled={disabled} data-testid={`usernameInput${field.name}`} placeholder={t('username')} />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  {...field}
                  name={[field.name, 'password']}
                  fieldKey={[field.key, 'password']}
                  label={t('password')}
                  rules={[
                    {
                      required: true,
                      message: `${t('password')} ${t('required')}`
                    }
                  ]}
                >
                  <Input
                    type="password"
                    disabled={disabled}
                    data-testid={`passwordInput${field.name}`}
                    placeholder={t('password')}
                  />
                </Form.Item>
              </Col>
            </>
          ) : null
        }
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues: IGeneralSettings, currentValues: IGeneralSettings) => {
          const currentServerInfo = currentValues.serverInfos?.[field.name];
          const prevServerInfo = prevValues.serverInfos?.[field.name];
          if (!currentServerInfo) {
            return false;
          }
          if (prevServerInfo?.loginType !== currentServerInfo.loginType) {
            form.setFields([{ name: ['serverInfos', field.name, 'token'], value: '' }]);
          }

          return prevServerInfo?.loginType !== currentServerInfo.loginType;
        }}
      >
        {({ getFieldValue }) =>
          getFieldValue(['serverInfos', field.name, 'loginType']) === 'token' ? (
            <Col span={24}>
              <Form.Item
                {...field}
                name={[field.name, 'token']}
                fieldKey={[field.key, 'token']}
                label={t('token')}
                rules={[
                  {
                    required: true,
                    message: `${t('token')} ${t('required')}`
                  }
                ]}
              >
                <Input disabled={disabled} data-testid={`tokenInput${field.name}`} placeholder={t('token')} />
              </Form.Item>
            </Col>
          ) : null
        }
      </Form.Item>
    </Row>
  );
};
